/**
 * /payroll-l2/allowances — Per diem, mileage, tool, uniform, and other allowances.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import type { AllowanceEntry, AllowanceType } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/allowances")({
  component: AllowancesPage,
});

const TYPE_LABELS: Record<AllowanceType | string, string> = {
  per_diem: "Per Diem",
  mileage: "Mileage",
  tool: "Tool",
  uniform: "Uniform",
  phone: "Phone",
  other: "Other",
};

function fmtMoney(v: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function AllowancesPage() {
  const [typeFilter, setTypeFilter] = useState<AllowanceType | "all">("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "allowances", typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (typeFilter !== "all") params.set("allowance_type", typeFilter);
      return await api<AllowanceEntry[]>(`/api/payroll-l2/allowance-entries?${params}`);
    },
  });

  const total = data?.reduce((sum, e) => sum + Number(e.amount), 0) ?? 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Allowances</Typography>
        <HelpHint docPath="payroll-l2/allowances.md" label="Allowances help" />
      </Stack>

      <Stack direction="row" alignItems="center" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value as AllowanceType | "all")}>
            <MenuItem value="all">All types</MenuItem>
            {(Object.keys(TYPE_LABELS) as AllowanceType[]).map((k) => (
              <MenuItem key={k} value={k}>{TYPE_LABELS[k]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {data && (
          <Typography variant="body2" color="text.secondary">
            {data.length} entries · Total: <strong>{fmtMoney(String(total))}</strong>
          </Typography>
        )}
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load allowances.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Approved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{entry.employee_id.slice(0, 8)}</TableCell>
                <TableCell>{entry.entry_date}</TableCell>
                <TableCell>
                  <Chip label={TYPE_LABELS[entry.allowance_type] ?? entry.allowance_type} size="small" />
                </TableCell>
                <TableCell>{entry.quantity}</TableCell>
                <TableCell>{fmtMoney(entry.rate)}</TableCell>
                <TableCell>{fmtMoney(entry.amount)}</TableCell>
                <TableCell>{entry.description ?? "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={entry.approved ? "Yes" : "Pending"}
                    color={entry.approved ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
